import type { ReactElement, ReactNode } from 'react';
import { cn } from '../felles/cn.js';

interface KortProps {
  tittel: string;
  /** Rendered as the accessible name of the section landmark. */
  beskrivelse?: string;
  children: ReactNode;
  /** Controls belonging to the panel, placed in its header band. */
  handling?: ReactNode;
  className?: string;
}

/**
 * A titled panel.
 *
 * Rendered as a `<section>` labelled by its own heading, so screen reader users
 * can navigate between panels by landmark instead of reading the page top to
 * bottom. A `<div>` with a bold line of text would look identical and give none
 * of that.
 */
export function Kort({
  tittel,
  beskrivelse,
  children,
  handling,
  className,
}: KortProps): ReactElement {
  const overskriftId = `kort-${tittel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <section
      aria-labelledby={overskriftId}
      className={cn('rounded-xl border border-kant bg-flate shadow-kort', className)}
    >
      {/* A tinted header band separates the panel's identity from its content.
          The tint is --kort-hode — the menus' cool tone, fainter — not the brand
          colour: a screen where every card shouts in indigo has no hierarchy
          left to spend. */}
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-t-xl border-b border-kant bg-kort-hode px-5 py-4">
        <div>
          <h2 id={overskriftId} className="text-base font-semibold text-tekst">
            {tittel}
          </h2>
          {beskrivelse ? <p className="mt-1 text-sm text-tekst-dempet">{beskrivelse}</p> : null}
        </div>
        {handling ?? null}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}
